// Auth0 Configuration
import { isLocalLoopbackHostname } from '../utils/companyTenantResolver';

// Get the current origin (works for both localhost and production)
// This MUST be a getter function to work at runtime, not build time
const getRedirectUri = () => {
  if (process.env.REACT_APP_AUTH0_REDIRECT_URI) {
    return process.env.REACT_APP_AUTH0_REDIRECT_URI;
  }
  // Use current origin (works for production domains automatically)
  // This is evaluated at runtime when Auth0Provider initializes
  if (typeof window !== 'undefined' && window.location) {
    return window.location.origin;
  }
  // Fallback for SSR or during build
  return 'http://localhost:3002';
};

// Get client ID dynamically based on environment
// ALWAYS check hostname first - ignore process.env which is set at build time
const getClientId = () => {
  // Auto-detect environment based on hostname at runtime (this is the source of truth)
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname;
    
    // Staging environment - ALWAYS use staging client ID
    if (hostname === 'staging.krossglobalmarketing.com' || hostname.includes('staging')) {
      return 'P4NCRrCFA6h7UZtnHNEnBHbyGzM7yJXx'; // Staging client ID
    }
    
    // Production environment
    if (hostname === 'krossglobalmarketing.com' || hostname === 'www.krossglobalmarketing.com') {
      return 'Ymq5KisXUcYdVTxnZKewnISyNO2JU81q'; // Production client ID
    }
    
    // Local development - default to staging for testing (IPv4 + IPv6 loopback)
    if (isLocalLoopbackHostname(hostname)) {
      return 'P4NCRrCFA6h7UZtnHNEnBHbyGzM7yJXx'; // Staging client ID for local dev
    }
  }
  
  // Default fallback to staging (safest for testing)
  return 'P4NCRrCFA6h7UZtnHNEnBHbyGzM7yJXx';
};

export const auth0Config = {
  domain: process.env.REACT_APP_AUTH0_DOMAIN || 'krossglobalmarketing.us.auth0.com', // Default Auth0 domain - use custom domain when ready
  get clientId() {
    // Use getter to ensure this is evaluated at runtime, not build time
    return getClientId();
  },
  audience: process.env.REACT_APP_AUTH0_AUDIENCE || undefined,
  get redirectUri() {
    // Use getter to ensure this is evaluated at runtime, not build time
    return getRedirectUri();
  },
  scope: 'openid profile email offline_access',
  useRefreshTokens: true,
  cacheLocation: 'localstorage',
  // Additional configuration options
  advancedOptions: {
    defaultScope: 'openid profile email offline_access'
  },
  // Customize the login experience
  authorizationParams: {
    response_type: 'code',
    prompt: 'login'
  }
};

// Helper function to check if Auth0 is properly configured
export const isAuth0Configured = () => {
  return !!(
    auth0Config.domain &&
    auth0Config.clientId && // This will use the getter
    auth0Config.redirectUri
  );
};

// Auth0 role mapping
export const AUTH0_ROLES = {
  ADMIN: 'admin',
  ADMINISTRATOR: 'administrator',
  MANAGER: 'manager', 
  OPERATOR: 'operator',
  VIEWER: 'viewer'
};

// Helper function to map Auth0 roles to our internal roles
export const mapAuth0RoleToInternal = (auth0Role) => {
  const normalizedRole = String(auth0Role || '').trim().toLowerCase();
  const roleMap = {
    [AUTH0_ROLES.ADMIN]: 'admin',
    [AUTH0_ROLES.ADMINISTRATOR]: 'admin',
    [AUTH0_ROLES.MANAGER]: 'manager',
    [AUTH0_ROLES.OPERATOR]: 'operator', 
    [AUTH0_ROLES.VIEWER]: 'viewer'
  };
  
  return roleMap[normalizedRole] || 'viewer'; // Default to viewer if role not found
};

export default auth0Config;
